import type { Context } from '../types/context';
import { debounce, getThemeSettings, isMobileWidth } from '../utils';

const shouldDebug = () =>
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

/** ================================
 *  Constants Registry
 *  ================================= */
export const CONSTANTS = {
  SELECTORS: {
    PREFIX: 'boost-sd__',

    BODY: 'body',

    // Elements we make sticky
    FILTER_TREE_VERTICAL_CONTENT:
      '#boost-sd-filter-tree-column .boost-sd__filter-tree-vertical-content',
    FILTER_TREE_VERTICAL_CONTENT_CLASS: '.boost-sd__filter-tree-vertical-content',
    FILTER_TREE_WRAPPER_VERTICAL_CONTENT:
      '#boost-sd__filter-tree-wrapper .boost-sd__filter-tree-vertical-content',
    FILTER_TREE_VERTIAL_OFF_CANVAS: '#boost-sd__filter-tree-vertical--style-off-canvas',
    FILTER_TREE_HORIZONTAL: '.boost-sd__filter-tree-horizontal',
    FILTER_TREE_HORIZONTAL_CONTENT: '.boost-sd__filter-tree-horizontal-content',

    // Toolbars / containers
    TOOLBAR_MOBILE: '.boost-sd__toolbar-top-mobile',
    TOOLBAR_CONTAINER: 'div[class^="boost-sd__toolbar-container"]',
    TOOLBAR_CONTAINER_CLASS: '.boost-sd__toolbar-container',
    TOOLBAR_CONTAINER_EXPAND: '.boost-sd__toolbar-container--style-expand',

    // End (boundary) elements
    END_ELEMENT:
      '.boost-sd__pagination-load-more-container, .boost-sd__pagination-infinite-scroll-container, .boost-sd__product-list',

    // Default skip
    DEFAULT_SKIP: '#PBarNextFrame',

    STICKY_BODY: `boost-sd__filter-tree-vertical-sticky-body`,
    TOOLBAR_STICKY_BODY: `boost-sd__toolbar-sticky`,
    HORIZONTAL_STICKY_BODY: `boost-sd__filter-tree-horizontal-sticky-body`,
  },

  CSS_CLASSES: {
    TOOLBAR_TOP_MOBILE: 'boost-sd__toolbar-top-mobile',
    CONTAINER_STYLE_EXPAND: 'container--style-expand',
    MOBILE_NAV_FIXED: 'mobile_nav-fixed--true',
    FILTER_TREE_VERTICAL_CONTENT: 'boost-sd__filter-tree-vertical-content',
    TOOLBAR_CONTAINER: 'boost-sd__toolbar-container',
    STICKY_STYLE_EXPAND: 'boost-sd__sticky-style-expand',
  },

  EVENTS: {
    PRODUCT_LIST_CHANGE: 'product-list-change',
    RESIZE_TRIGGER: 'resize-trigger-sticky-event',
    SCROLL: 'scroll',
    TRANSITION_END: 'transitionend',
    ANIMATION_END: 'animationend',
    DOM_CONTENT_LOADED: 'DOMContentLoaded',
    MUTATION: 'mutation',
  },

  ATTRIBUTES: {
    OFFSET_TOP: 'data-offset-top',
    STICKY_ID: 'data-sticky-id',
  },

  THRESHOLDS: {
    MAX_OFFSET_GUARD: 1500,
    MIN_WIDTH_RATIO: 0.5,
    POSITION_TOLERANCE: 1,
    ANIMATION_DELAY: 10,
    ANIMATION_TRANSITION_DELAY: 0,
    MUTATION_DEBOUNCE: 0,
    // Event debounce delays (ms)
    PRODUCT_LIST_CHANGE_DEBOUNCE: 100,
    RESIZE_TRIGGER_DEBOUNCE: 150,
    SCROLL_DEBOUNCE: 8, // 30fps
  },

  STYLES: {
    BACKGROUND_COLOR: '#ffffff',
    MARGIN: '0 auto',
    FULL_WIDTH: '100%',
    INHERIT: 'inherit',
  },

  Z_INDEX: {
    BASE: 2,
    TOOLBAR: 3,
    MOBILE_BOOST: 1,
  },
} as const;

/** ================================
 *  Types & Interfaces
 *  ================================= */
export interface StickyConfig {
  elements: {
    stickyElement: string;
    endElement: string;
    skipSelector?: string[];
    preDefineSelector?: string;
  };

  styling: {
    customStyle?: string;
    isFullWidth?: boolean;
    isMaxWidth?: boolean;
    isFullHeight?: boolean;
  };

  behavior: {
    defaultOffsetTop?: number;
    ignoreCheckWidth?: boolean;
    isToolbarIndex?: boolean;
    isOpeningFilterTree?: boolean;
    watchForDOMChanges?: boolean; // New option
  };

  hooks: {
    callBack?: (isSticky: boolean) => void;
    customizeSticky?: (args: any) => any;
    onElementsChanged?: (newElements: HTMLElement[], oldElements: HTMLElement[]) => void; // New hook
  };

  debug?: boolean;
}

export interface StickyState {
  elements: {
    sticky: HTMLElement[];
    avoid: HTMLElement[];
    end: HTMLElement | null;
    first: HTMLElement | null;
  };

  dimensions: {
    originalWidth: number;
    stickyHeight: number;
    offsetTop: number;
  };

  positions: {
    startSticky: number;
    endSticky: number;
    currentScroll: number;
  };

  flags: {
    isMobileStyle: boolean;
    isDesktopHorizontal: boolean;
    isSticky: boolean;
    elementsChanged: boolean; // New flag
  };
}

export interface StickyOverrides {
  calculateOffsetTop?: (state: StickyState, config: StickyConfig) => number;
  determineAvoidElements?: (config: StickyConfig, state: StickyState) => HTMLElement[];
  generateStyles?: (state: StickyState, config: StickyConfig) => string[];
  shouldStick?: (scrollY: number, start: number, end: number) => 'static' | 'fixed' | 'absolute';
}

export type StickyOverrideConfig = {
  elements?: Partial<StickyConfig['elements']> & {
    skipSelector?: string[];
    skipSelectorMergeMode?: 'replace' | 'merge' | 'prepend';
  };
  styling?: Partial<StickyConfig['styling']>;
  behavior?: Partial<StickyConfig['behavior']>;
  hooks?: Partial<StickyConfig['hooks']>;
  debug?: boolean;
};

/** ================================
 *  Utility Functions
 *  ================================= */
const qs = <T extends Element = HTMLElement>(sel: string, root: Document | Element = document) =>
  root.querySelector<T>(sel);

const qsa = (sel: string, root: Document | Element = document) =>
  Array.from(root.querySelectorAll<HTMLElement>(sel));

/** ================================
 *  Dynamic Element Management
 *  ================================= */

/**
 * Checks if elements in the DOM have changed compared to cached elements
 */
function detectElementChanges(
  config: StickyConfig,
  cachedElements: HTMLElement[]
): { changed: boolean; newElements: HTMLElement[] } {
  const currentElements = qsa(config.elements.stickyElement);

  // Check if count changed
  if (currentElements.length !== cachedElements.length) {
    return { changed: true, newElements: currentElements };
  }

  // Check if any elements are different
  const changed = currentElements.some((el, index) => {
    const cached = cachedElements[index];
    return !cached || el !== cached || !document.contains(cached);
  });

  return { changed, newElements: currentElements };
}

/**
 * Validates and refreshes element references
 */
function refreshElementReferences(state: StickyState, config: StickyConfig): boolean {
  const detection = detectElementChanges(config, state.elements.sticky);

  if (detection.changed) {
    const oldElements = [...state.elements.sticky];
    state.elements.sticky = detection.newElements;
    state.elements.first = detection.newElements[0] || null;
    state.elements.end = qs(config.elements.endElement);
    state.flags.elementsChanged = true;

    // Trigger callback if provided
    config.hooks.onElementsChanged?.(detection.newElements, oldElements);

    if (config.debug) {
      console.debug('[sticky] Elements changed:', {
        old: oldElements.length,
        new: detection.newElements.length,
        elements: detection.newElements,
      });
    }

    return true;
  }
  return false;
}

/**
 * Assigns unique IDs to sticky elements for tracking
 */
function assignStickyIds(elements: HTMLElement[]): void {
  elements.forEach((el, index) => {
    if (!el.hasAttribute(CONSTANTS.ATTRIBUTES.STICKY_ID)) {
      el.setAttribute(CONSTANTS.ATTRIBUTES.STICKY_ID, `sticky-${Date.now()}-${index}`);
    }
  });
}

/** ================================
 *  Core Sticky Functions (Enhanced)
 *  ================================= */

/**
 * Enhanced validation that handles dynamic elements
 */
function validateStickySetup(config: StickyConfig): { isValid: boolean; error?: string } {
  const elements = qsa(config.elements.stickyElement);

  if (!elements.length) {
    return { isValid: false, error: 'Sticky element not found' };
  }

  // Check if any elements are detached from DOM
  const attachedElements = elements.filter((el) => document.contains(el));
  if (attachedElements.length === 0) {
    return { isValid: false, error: 'No sticky elements are attached to DOM' };
  }

  if (!config.behavior.ignoreCheckWidth) {
    const visibleElements = attachedElements.filter((el) => el.getBoundingClientRect()?.width > 0);
    if (visibleElements.length === 0) {
      return { isValid: false, error: 'No sticky elements are visible in viewport' };
    }
  }

  return { isValid: true };
}

/**
 * Enhanced initialization with dynamic element tracking
 */
function initializeStickyState(config: StickyConfig): StickyState {
  const stickyElements = qsa(config.elements.stickyElement).filter((el) => document.contains(el));
  const firstElement = stickyElements[0] || null;
  const endElement = qs(config.elements.endElement);

  // Assign tracking IDs
  assignStickyIds(stickyElements);

  const originalWidth = firstElement ? Math.floor(firstElement.getBoundingClientRect().width) : 0;

  return {
    elements: {
      sticky: stickyElements,
      avoid: [],
      end: endElement,
      first: firstElement,
    },
    dimensions: {
      originalWidth,
      stickyHeight: firstElement?.scrollHeight || 0,
      offsetTop: 0,
    },
    positions: {
      startSticky: 0,
      endSticky: 0,
      currentScroll: window.scrollY,
    },
    flags: {
      isMobileStyle:
        firstElement?.classList.contains(CONSTANTS.CSS_CLASSES.TOOLBAR_TOP_MOBILE) || false,
      isDesktopHorizontal: firstElement
        ? Array.from(firstElement.classList).some((c) =>
            c.endsWith(CONSTANTS.CSS_CLASSES.CONTAINER_STYLE_EXPAND)
          )
        : false,
      isSticky: false,
      elementsChanged: false,
    },
  };
}

/**
 * Enhanced offset calculation that handles element changes
 */
function calculateInitialOffsetTop(state: StickyState, config: StickyConfig): void {
  const { first } = state.elements;
  if (!first || !document.contains(first)) return;

  // Re-calculate if elements changed or offset is missing
  if (
    state.flags.elementsChanged ||
    !first.hasAttribute(CONSTANTS.ATTRIBUTES.OFFSET_TOP) ||
    first.getAttribute(CONSTANTS.ATTRIBUTES.OFFSET_TOP) === '0' ||
    !first.getAttribute(CONSTANTS.ATTRIBUTES.OFFSET_TOP)
  ) {
    let dataOffsetTopOrigin = first.getBoundingClientRect().top;

    // Guard against extremely large values
    if (dataOffsetTopOrigin > CONSTANTS.THRESHOLDS.MAX_OFFSET_GUARD) return;

    // Handle scroll position
    if (window.scrollY > 0) {
      dataOffsetTopOrigin += window.scrollY;
    }

    first.setAttribute(CONSTANTS.ATTRIBUTES.OFFSET_TOP, String(Math.floor(dataOffsetTopOrigin)));
  }
}
/**
 * Determines which elements should be avoided (headers, fixed elements)
 */
function determineAvoidElements(config: StickyConfig, state: StickyState): HTMLElement[] {
  const avoid: HTMLElement[] = [];
  const { preDefineSelector } = config.elements;
  const skipSelector = [...(config.elements.skipSelector || []), CONSTANTS.SELECTORS.DEFAULT_SKIP];
  // Add pre-defined selectors first
  if (preDefineSelector) {
    qsa(preDefineSelector).forEach((el) => {
      if (!avoid.includes(el)) {
        avoid.push(el);
      }
    });
  }

  // Find positioned elements that could interfere
  const candidates = findPositionedCandidates(skipSelector);

  // Group elements by vertical position
  const groupedElements = groupElementsByPosition(candidates);

  // Deduplicate nested elements
  return deduplicateNestedElements([...avoid, ...groupedElements]);
}

/**
 * Finds elements with fixed/sticky/absolute positioning that could interfere
 */
function findPositionedCandidates(skipSelectors: string[]): HTMLElement[] {
  const candidates: HTMLElement[] = [];

  for (const el of qsa('*')) {
    const style = getComputedStyle(el);
    if (style.visibility === 'hidden' || style.display === 'none') continue;

    if (['fixed', 'sticky', 'absolute'].includes(style.position)) {
      const rect = el.getBoundingClientRect();
      if (
        rect.height > 0 &&
        rect.width >= window.innerWidth * CONSTANTS.THRESHOLDS.MIN_WIDTH_RATIO
      ) {
        // Check if element matches any of the skip selectors
        const shouldSkip = skipSelectors.some((selector) => el.matches(selector));
        if (!shouldSkip) {
          candidates.push(el);
        }
      }
    }
  }

  return candidates.sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);
}

/**
 * Groups elements by their vertical position to handle overlapping elements
 */
function groupElementsByPosition(candidates: HTMLElement[]): HTMLElement[] {
  const grouped: HTMLElement[] = [];
  let currentY = 0;
  let i = 0;

  while (i < candidates.length) {
    const group: HTMLElement[] = [];
    const allInGroup: HTMLElement[] = [];
    const groupTop = candidates[i].getBoundingClientRect().top;

    if (Math.abs(groupTop - currentY) > CONSTANTS.THRESHOLDS.POSITION_TOLERANCE) break;

    // Collect elements at the same vertical position
    while (
      i < candidates.length &&
      Math.abs(candidates[i].getBoundingClientRect().top - groupTop) <=
        CONSTANTS.THRESHOLDS.POSITION_TOLERANCE
    ) {
      const cand = candidates[i];
      allInGroup.push(cand);
      if (!grouped.includes(cand)) {
        group.push(cand);
      }
      i++;
    }

    if (allInGroup.length > 0) {
      const maxBottom = Math.max(...allInGroup.map((g) => g.getBoundingClientRect().bottom));
      currentY = Math.round(maxBottom);
      grouped.push(...group);
    }
  }

  return grouped;
}

/**
 * Removes nested elements, keeping the larger parent element
 */
function deduplicateNestedElements(elements: HTMLElement[]): HTMLElement[] {
  const result = [...elements];
  let changed = true;

  while (changed) {
    changed = false;
    for (let i = 0; i < result.length; i++) {
      for (let j = i + 1; j < result.length; j++) {
        const el1 = result[i];
        const el2 = result[j];

        if (el1.contains(el2) || el2.contains(el1)) {
          const r1 = el1.getBoundingClientRect();
          const r2 = el2.getBoundingClientRect();
          const keepEl = r1.height >= r2.height ? el1 : el2;
          const removeIdx = keepEl === el1 ? j : i;

          result.splice(removeIdx, 1);
          changed = true;
          break;
        }
      }
      if (changed) break;
    }
  }

  return result;
}

/**
 * Calculates the total offset from avoid elements
 */
function calculateOffsetTop(avoidElements: HTMLElement[]): number {
  const checkClassIgnored = (element: Element): boolean => {
    const ignoredClasses: string[] = [CONSTANTS.CSS_CLASSES.MOBILE_NAV_FIXED];
    return Array.from(element.classList).some((c) => ignoredClasses.includes(c));
  };

  let offsetTop = 0;

  for (const header of avoidElements) {
    const rect = header.getBoundingClientRect();

    if (rect.y >= -1 && rect.height > 0) {
      offsetTop += rect.height;
    } else if (header.children?.length > 0) {
      for (const child of Array.from(header.children)) {
        const childRect = (child as HTMLElement).getBoundingClientRect();
        if (childRect.y >= 0 && childRect.height > 0 && !checkClassIgnored(child)) {
          offsetTop += childRect.height;
        }
      }
    }
  }

  return Math.floor(offsetTop);
}

/**
 * Updates positions based on current scroll and element positions
 */
function updatePositions(state: StickyState, config: StickyConfig): void {
  const { first, end } = state.elements;
  if (!first) return;

  // Update start position
  let startPosSticky = Number(first.getAttribute(CONSTANTS.ATTRIBUTES.OFFSET_TOP) || 0);

  if (first.getBoundingClientRect().top > startPosSticky) {
    const newOffset =
      config.behavior.defaultOffsetTop || first.getBoundingClientRect().top + window.scrollY;

    first.setAttribute(CONSTANTS.ATTRIBUTES.OFFSET_TOP, String(newOffset));
    startPosSticky = newOffset;
  }

  // Calculate end position
  const endPosSticky = end
    ? end.getBoundingClientRect().top + window.scrollY + end.scrollHeight
    : 0;

  // Update state
  state.positions.startSticky = startPosSticky - state.dimensions.offsetTop;
  state.positions.endSticky =
    endPosSticky -
    state.dimensions.offsetTop -
    Math.min(state.dimensions.stickyHeight, window.innerHeight - state.dimensions.offsetTop);
  state.positions.currentScroll = window.scrollY;
}

/**
 * Determines the positioning behavior based on scroll position
 */
function determineStickyBehavior(
  state: StickyState,
  config: StickyConfig
): 'static' | 'fixed' | 'absolute' {
  const { currentScroll } = state.positions;
  const { startSticky, endSticky } = state.positions;
  const { isMobileStyle, isDesktopHorizontal } = state.flags;

  const alwaysStickyWhenOpening =
    config.behavior.isOpeningFilterTree &&
    (isMobileStyle || isDesktopHorizontal) &&
    startSticky <= currentScroll;

  if (!alwaysStickyWhenOpening && currentScroll < startSticky) {
    return 'static';
  } else if (
    alwaysStickyWhenOpening ||
    (startSticky <= currentScroll && currentScroll <= endSticky)
  ) {
    return 'fixed';
  } else {
    return 'absolute';
  }
}

/**
 * Generates CSS styles based on positioning behavior
 */
function generateStickyStyles(state: StickyState, config: StickyConfig): string[] {
  const behavior = determineStickyBehavior(state, config);
  const styles: string[] = [];

  // Common properties
  const widthSticky = config.styling.isFullWidth
    ? CONSTANTS.STYLES.FULL_WIDTH
    : CONSTANTS.STYLES.INHERIT;
  const maxWidthSticky = config.styling.isMaxWidth
    ? CONSTANTS.STYLES.FULL_WIDTH
    : `${state.dimensions.originalWidth}px`;

  let zIndex = config.behavior.isToolbarIndex ? CONSTANTS.Z_INDEX.TOOLBAR : CONSTANTS.Z_INDEX.BASE;
  if (state.flags.isMobileStyle) {
    zIndex += CONSTANTS.Z_INDEX.MOBILE_BOOST;
  }

  switch (behavior) {
    case 'static':
      styles.push('position: static');
      break;

    case 'fixed':
      styles.push(
        'position: fixed',
        `max-height: ${window.innerHeight - state.dimensions.offsetTop}px`,
        `top: ${state.dimensions.offsetTop}px`,
        `z-index: ${zIndex}`,
        `width: ${widthSticky}`,
        `max-width: ${maxWidthSticky}`,
        `background-color: ${CONSTANTS.STYLES.BACKGROUND_COLOR}`,
        `margin: ${CONSTANTS.STYLES.MARGIN}`,
        config.styling.customStyle || ''
      );

      if (config.styling.isFullHeight) {
        styles.push('height: 100%', 'max-height: unset');
      }
      if (config.styling.isFullWidth) {
        styles.push('left: 0', 'right: 0');
      }
      break;

    case 'absolute':
      if (isMobileWidth()) {
        styles.push(
          'position: absolute',
          `max-height: ${window.innerHeight - state.dimensions.offsetTop}px`,
          'top: -100%',
          'left: 0',
          'right: 0',
          `z-index: ${zIndex}`,
          `width: ${widthSticky}`,
          `max-width: ${maxWidthSticky}`,
          `background-color: ${CONSTANTS.STYLES.BACKGROUND_COLOR}`,
          `margin: ${CONSTANTS.STYLES.MARGIN}`,
          config.styling.customStyle || ''
        );
      } else {
        styles.push(
          'position: absolute',
          'max-height: unset',
          'bottom: 100px',
          `z-index: ${zIndex}`,
          `width: ${widthSticky}`,
          `max-width: ${maxWidthSticky}`,
          `background-color: ${CONSTANTS.STYLES.BACKGROUND_COLOR}`,
          `margin: ${CONSTANTS.STYLES.MARGIN}`,
          config.styling.customStyle || ''
        );

        if (config.styling.isFullWidth) {
          styles.push('left: 0', 'right: 0');
        }
      }
      break;
  }

  return styles;
}

/**
 * Applies the sticky styles and triggers callbacks
 */
function applyStickyBehavior(state: StickyState, config: StickyConfig, styles: string[]): void {
  const behavior = determineStickyBehavior(state, config);
  const isSticky = behavior !== 'static';

  // Apply styles
  state.elements.first?.setAttribute('style', styles.join(';'));

  // Update state
  state.flags.isSticky = isSticky;

  // Trigger callback
  config.hooks.callBack?.(isSticky);

  // Debug logging
  if (config.debug) {
    console.debug('[sticky] behavior:', behavior, 'styles:', styles);
  }
}

/**
 * Enhanced position update with element validation
 */
function updateStickyPosition(
  state: StickyState,
  config: StickyConfig,
  overrides?: StickyOverrides
): void {
  // First, refresh and validate element references
  const elementsChanged = refreshElementReferences(state, config);

  if (!state.elements.first || !document.contains(state.elements.first)) {
    if (config.debug) {
      console.debug('[sticky] Primary element not found or detached');
    }
    return;
  }

  requestAnimationFrame(() => {
    // Re-calculate initial offset if elements changed
    if (elementsChanged) {
      calculateInitialOffsetTop(state, config);

      // Update dimensions
      const rect = state.elements.first?.getBoundingClientRect();
      if (rect && rect.width > 0) {
        state.dimensions.originalWidth = Math.floor(rect.width);
        state.dimensions.stickyHeight = state.elements.first?.scrollHeight || 0;
      }
    }

    // Determine avoid elements
    const avoidElements =
      overrides?.determineAvoidElements?.(config, state) ?? determineAvoidElements(config, state);
    state.elements.avoid = avoidElements;

    // Calculate offset
    state.dimensions.offsetTop =
      overrides?.calculateOffsetTop?.(state, config) ?? calculateOffsetTop(avoidElements);

    // Update positions
    updatePositions(state, config);

    // Generate styles
    const styles =
      overrides?.generateStyles?.(state, config) ?? generateStickyStyles(state, config);

    // Apply behavior
    applyStickyBehavior(state, config, styles);

    // Reset the changed flag
    state.flags.elementsChanged = false;
  });
}

/** ================================
 *  Enhanced Event Management
 *  ================================= */
class DynamicEventManager {
  private listeners: Map<string, () => void> = new Map();
  private debouncedListeners: Map<string, () => void> = new Map(); // Store debounced functions per event
  private transitionListeners: Map<
    HTMLElement,
    { transitionend: () => void; animationend: () => void }
  > = new Map();
  private mutationObserver: MutationObserver | null = null;
  private debouncedUpdate: (() => void) | null = null;
  private isExecuting: boolean = false;

  setupMainListeners(updateFn: () => void): void {
    const events = [
      CONSTANTS.EVENTS.PRODUCT_LIST_CHANGE,
      CONSTANTS.EVENTS.RESIZE_TRIGGER,
      CONSTANTS.EVENTS.SCROLL,
    ];

    // Define debounce delays per event type
    const eventDebounceDelays: Record<string, number> = {
      [CONSTANTS.EVENTS.PRODUCT_LIST_CHANGE]: CONSTANTS.THRESHOLDS.PRODUCT_LIST_CHANGE_DEBOUNCE,
      [CONSTANTS.EVENTS.RESIZE_TRIGGER]: CONSTANTS.THRESHOLDS.RESIZE_TRIGGER_DEBOUNCE,
      [CONSTANTS.EVENTS.SCROLL]: CONSTANTS.THRESHOLDS.SCROLL_DEBOUNCE,
    };

    events.forEach((eventName) => {
      this.removeListener(eventName);

      const wrappedUpdateFn = () => {
        // Prevent concurrent execution
        if (this.isExecuting) {
          if (shouldDebug()) {
            console.log(`[sticky] Event ${eventName} skipped - already executing`);
          }
          return;
        }

        this.isExecuting = true;
        if (shouldDebug()) {
          console.log(`[sticky] Event executed: ${eventName}`);
        }

        try {
          updateFn();
        } finally {
          this.isExecuting = false;
        }
      };

      // Create or reuse debounced function for this event type
      const delay = eventDebounceDelays[eventName] || 50;
      const debouncedFn = debounce(() => {
        if (shouldDebug()) {
          console.log(`[sticky] Event debounced (${delay}ms): ${eventName}`);
        }
        wrappedUpdateFn();
      }, delay);

      window.addEventListener(eventName, debouncedFn);
      this.listeners.set(eventName, debouncedFn);
      this.debouncedListeners.set(eventName, debouncedFn);
    });
  }

  setupMutationObserver(config: StickyConfig, updateFn: () => void): void {
    if (!config.behavior.watchForDOMChanges) return;

    this.debouncedUpdate = debounce(() => {
      // Prevent concurrent execution
      if (this.isExecuting) {
        if (config.debug) {
          console.log('[sticky] DOM_MUTATION event skipped - already executing');
        }
        return;
      }

      this.isExecuting = true;
      if (config.debug) {
        console.log('[sticky] Event triggered: DOM_MUTATION');
      }

      try {
        updateFn();
      } finally {
        this.isExecuting = false;
      }
    }, CONSTANTS.THRESHOLDS.MUTATION_DEBOUNCE);

    this.mutationObserver = new MutationObserver((mutations) => {
      let shouldUpdate = false;

      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Check if sticky elements were added/removed
          const addedNodes = Array.from(mutation.addedNodes) as Node[];
          const removedNodes = Array.from(mutation.removedNodes) as Node[];

          const relevantChange = [...addedNodes, ...removedNodes].some((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              return (
                element.matches(config.elements.stickyElement) ||
                element.querySelector(config.elements.stickyElement) ||
                element.matches(config.elements.endElement) ||
                element.querySelector(config.elements.endElement)
              );
            }
            return false;
          });

          if (relevantChange) {
            shouldUpdate = true;
            break;
          }
        }
      }

      if (shouldUpdate && this.debouncedUpdate) {
        this.debouncedUpdate();
      }
    });

    // Observe the document body for changes
    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  setupTransitionListeners(elements: HTMLElement[], updateFn: () => void): void {
    if (!elements || !Array.isArray(elements)) {
      console.warn('[sticky] Invalid elements array provided to setupTransitionListeners');
      return;
    }

    // Clean up old listeners
    this.cleanupTransitionListeners();

    elements
      .filter((el) => el && document.contains(el)) // Ensure elements exist and are in DOM
      .forEach((el) => {
        try {
          const style = getComputedStyle(el);
          if (
            style.transition &&
            style.transition !== 'none' &&
            style.transition !== 'all 0s ease 0s'
          ) {
            const delayedUpdate = () => {
              setTimeout(() => {
                // Prevent concurrent execution
                if (this.isExecuting) {
                  if (shouldDebug()) {
                    console.log('[sticky] TRANSITION_END event skipped - already executing');
                  }
                  return;
                }

                this.isExecuting = true;
                try {
                  updateFn();
                } finally {
                  this.isExecuting = false;
                }
              }, CONSTANTS.THRESHOLDS.ANIMATION_TRANSITION_DELAY);
            };
            const delayedAnimationUpdate = () => {
              setTimeout(() => {
                // Prevent concurrent execution
                if (this.isExecuting) {
                  if (shouldDebug()) {
                    console.debug('[sticky] ANIMATION_END event skipped - already executing');
                  }
                  return;
                }

                this.isExecuting = true;

                try {
                  updateFn();
                } finally {
                  this.isExecuting = false;
                }
              }, CONSTANTS.THRESHOLDS.ANIMATION_DELAY);
            };
            const listeners = {
              transitionend: delayedUpdate,
              animationend: delayedAnimationUpdate,
            };

            el.addEventListener(CONSTANTS.EVENTS.TRANSITION_END, listeners.transitionend);
            el.addEventListener(CONSTANTS.EVENTS.ANIMATION_END, listeners.animationend);
            this.transitionListeners.set(el, listeners);
          }
        } catch (error) {
          if (shouldDebug()) {
            console.warn('[sticky] Error setting up transition listeners for element:', el, error);
          }
        }
      });
  }

  private removeListener(eventName: string): void {
    const existingListener = this.listeners.get(eventName);
    if (existingListener) {
      window.removeEventListener(eventName, existingListener);
      this.listeners.delete(eventName);
    }
  }

  private cleanupTransitionListeners(): void {
    this.transitionListeners.forEach((listeners, el) => {
      // Check if element still exists in DOM before removing listeners
      if (el && typeof el.removeEventListener === 'function') {
        try {
          el.removeEventListener(CONSTANTS.EVENTS.TRANSITION_END, listeners.transitionend);
          el.removeEventListener(CONSTANTS.EVENTS.ANIMATION_END, listeners.animationend);
        } catch (error) {
          if (shouldDebug()) {
            console.warn('[sticky] Error removing transition listeners:', error);
          }
        }
      }
    });
    this.transitionListeners.clear();
  }

  cleanup(): void {
    // Remove main listeners
    this.listeners.forEach((listener, eventName) => {
      window.removeEventListener(eventName, listener);
    });
    this.listeners.clear();
    this.debouncedListeners.clear(); // Clear debounced functions cache

    // Remove transition listeners
    this.cleanupTransitionListeners();

    // Disconnect mutation observer
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    this.debouncedUpdate = null;
  }

  get hasTransitionListeners(): boolean {
    return this.transitionListeners.size > 0;
  }
}

/** ================================
 *  Enhanced Sticky Controller
 *  ================================= */
export class DynamicStickyController {
  private state: StickyState;
  protected config: StickyConfig;
  private overrides?: StickyOverrides;
  private eventManager: DynamicEventManager;
  private debouncedPositionUpdate?: () => void;

  constructor(config: StickyConfig, overrides?: StickyOverrides) {
    this.config = {
      ...config,
      behavior: {
        watchForDOMChanges: true, // Default to true for dynamic behavior
        ...config.behavior,
      },
    };
    this.overrides = overrides;
    this.eventManager = new DynamicEventManager();
    this.state = initializeStickyState(this.config);
  }

  initialize(): boolean {
    // Validate setup
    const validation = validateStickySetup(this.config);
    if (!validation.isValid) {
      if (this.config.debug) {
        console.warn('[sticky] Setup validation failed:', validation.error);
      }
      return false;
    }

    // Check for custom implementation
    if (this.config.hooks.customizeSticky) {
      return this.config.hooks.customizeSticky({
        stickyElement: this.config.elements.stickyElement,
        endElement: this.config.elements.endElement,
        ...this.config.styling,
        ...this.config.behavior,
        preDefineSelector: this.config.elements.preDefineSelector,
        defaultOffsetTop: this.config.behavior.defaultOffsetTop || 0,
      });
    }

    // Initialize state
    this.state = initializeStickyState(this.config);
    calculateInitialOffsetTop(this.state, this.config);

    // Setup event listeners
    const updateFn = () => this.updatePosition();
    this.eventManager.setupMainListeners(updateFn);
    this.eventManager.setupMutationObserver(this.config, updateFn);

    // Initial position update
    this.updatePosition();

    return true;
  }

  updatePosition(): void {
    updateStickyPosition(this.state, this.config, this.overrides);

    // Setup transition listeners for avoid elements (only if elements have changed)
    if (this.state.flags.elementsChanged || !this.eventManager.hasTransitionListeners) {
      this.eventManager.setupTransitionListeners(this.state.elements.avoid, () => {
        // Create a debounced version to prevent rapid successive calls
        if (!this.debouncedPositionUpdate) {
          this.debouncedPositionUpdate = debounce(() => {
            updateStickyPosition(this.state, this.config, this.overrides);
          }, 10);
        }
        this.debouncedPositionUpdate();
      });
    }
  }

  updateConfig(newConfig: Partial<StickyConfig>): void {
    this.config = { ...this.config, ...newConfig };

    // If DOM watching behavior changed, update mutation observer
    if (newConfig.behavior?.watchForDOMChanges !== undefined) {
      this.eventManager.cleanup();
      const updateFn = () => this.updatePosition();
      this.eventManager.setupMainListeners(updateFn);
      this.eventManager.setupMutationObserver(this.config, updateFn);
    }

    this.updatePosition();
  }

  // Public method to get config (for external access)
  getConfig(): Readonly<StickyConfig> {
    return { ...this.config };
  }

  getState(): Readonly<StickyState> {
    return { ...this.state };
  }

  // Force refresh elements (useful when you know DOM has changed)
  forceRefresh(): void {
    this.state.flags.elementsChanged = true;
    this.updatePosition();
  }

  destroy(): void {
    this.eventManager.cleanup();

    // Reset styles on all tracked elements
    this.state.elements.sticky.forEach((el) => {
      if (document.contains(el)) {
        el.removeAttribute('style');
        el.removeAttribute(CONSTANTS.ATTRIBUTES.OFFSET_TOP);
        el.removeAttribute(CONSTANTS.ATTRIBUTES.STICKY_ID);
      }
    });
  }
}

/** ================================
 *  Updated Legacy Function Wrapper
 *  ================================= */
export function dynamicSticky(params: {
  stickyElement: string;
  endElement: string;
  isFullWidth?: boolean;
  isMaxWidth?: boolean;
  isToolbarIndex?: boolean;
  customStyle?: string;
  isFullHeight?: boolean;
  ignoreCheckWidth?: boolean;
  callBack?: (isSticky: boolean) => void;
  isOpeningFilterTree?: boolean;
  customizeSticky?: (args: any) => any;
  preDefineSelector?: string;
  skipSelector?: string[];
  debug?: boolean;
  defaultOffsetTop?: number;
  watchForDOMChanges?: boolean;
}): DynamicStickyController | undefined {
  const config: StickyConfig = {
    elements: {
      stickyElement: params.stickyElement,
      endElement: params.endElement,
      skipSelector: params.skipSelector,
      preDefineSelector: params.preDefineSelector,
    },
    styling: {
      customStyle: params.customStyle,
      isFullWidth: params.isFullWidth,
      isMaxWidth: params.isMaxWidth,
      isFullHeight: params.isFullHeight,
    },
    behavior: {
      defaultOffsetTop: params.defaultOffsetTop,
      ignoreCheckWidth: params.ignoreCheckWidth,
      isToolbarIndex: params.isToolbarIndex,
      isOpeningFilterTree: params.isOpeningFilterTree,
      watchForDOMChanges: params.watchForDOMChanges ?? true,
    },
    hooks: {
      callBack: params.callBack,
      customizeSticky: params.customizeSticky,
    },
    debug: params.debug,
  };

  const controller = new DynamicStickyController(config);
  const success = controller.initialize();

  return success ? controller : undefined;
}

// Backward compatibility exports
export { DynamicStickyController as StickyController };
export { dynamicSticky as sticky };

// Type definition for filter settings structure
interface FilterSettingsWithSwatch {
  swatch_settings?: unknown;
  [key: string]: unknown;
}

// Helper function to get persisted offset values
export const getFilterSettings = (context: Context): any => {
  if (!context) {
    console.error('context getFilterSettings null');
    return {};
  }

  // swatch_settings get from filterSettings app admin
  const filterSettings = context?.app?.filterSettings;
  const swatchSettings =
    typeof filterSettings === 'object' &&
    filterSettings !== null &&
    'swatch_settings' in filterSettings
      ? (filterSettings as FilterSettingsWithSwatch).swatch_settings
      : undefined;

  return {
    ...(context?.templateSettings?.filterSettings || {}),
    swatch_settings: swatchSettings,
  };
};
export const persistDataOffsetTop = (context: Context) => {
  const [, setStickyOffset] = context.useContextState('stickyOffset', '');
  const stickyOffset = getPersistedOffsetValues(context);
  const content = context.$(CONSTANTS.SELECTORS.FILTER_TREE_VERTICAL_CONTENT_CLASS) as
    | Element
    | undefined;
  let offsetTopValue = 0;
  if (content) {
    if (
      !content.hasAttribute(CONSTANTS.ATTRIBUTES.OFFSET_TOP) ||
      content.getAttribute(CONSTANTS.ATTRIBUTES.OFFSET_TOP) === '0' ||
      !content.getAttribute(CONSTANTS.ATTRIBUTES.OFFSET_TOP)
    ) {
      let dataOffsetTopOrigin = content.getBoundingClientRect().top;
      // TEMP guard (original logic kept)
      if (dataOffsetTopOrigin > 1500) return;
      // Cover case: scroll down & reload
      if (window.scrollY > 0) dataOffsetTopOrigin += window.scrollY;
      offsetTopValue = Math.floor(dataOffsetTopOrigin);
      content.setAttribute(CONSTANTS.ATTRIBUTES.OFFSET_TOP, `${offsetTopValue}`);
    }
  }

  const offsetContentTopValue = content?.getAttribute(CONSTANTS.ATTRIBUTES.OFFSET_TOP);
  stickyOffset.stickyOffsetContentTop = Number(offsetContentTopValue);

  const toolbarTopMobile = context.$(CONSTANTS.SELECTORS.TOOLBAR_MOBILE) as Element | undefined;
  if (toolbarTopMobile) {
    if (
      !toolbarTopMobile.hasAttribute(CONSTANTS.ATTRIBUTES.OFFSET_TOP) ||
      toolbarTopMobile.getAttribute(CONSTANTS.ATTRIBUTES.OFFSET_TOP) === '0' ||
      !toolbarTopMobile.getAttribute(CONSTANTS.ATTRIBUTES.OFFSET_TOP)
    ) {
      let dataOffsetTopOrigin = toolbarTopMobile.getBoundingClientRect().top;
      // TEMP guard (original logic kept)
      if (dataOffsetTopOrigin > 1500) return;
      // Cover case: scroll down & reload
      if (window.scrollY > 0) dataOffsetTopOrigin += window.scrollY;
      offsetTopValue = Math.floor(dataOffsetTopOrigin);
      toolbarTopMobile.setAttribute(CONSTANTS.ATTRIBUTES.OFFSET_TOP, `${offsetTopValue}`);
    }
  }

  const offsetToolbarTopValue = toolbarTopMobile?.getAttribute(CONSTANTS.ATTRIBUTES.OFFSET_TOP);

  stickyOffset.stickyOffsetTop = Number(offsetToolbarTopValue);

  setStickyOffset(JSON.stringify(stickyOffset));
};

function getPersistedOffsetValues(context: Context): {
  stickyOffsetTop: number;
  stickyOffsetContentTop: number;
} {
  const [getStickyOffset] = context.useContextState('stickyOffset', '');
  const stickyOffsetValue = getStickyOffset();
  const stickyOffset = stickyOffsetValue ? JSON.parse(stickyOffsetValue) : {};
  const stickyOffsetTop = stickyOffset?.stickyOffsetTop || 0;
  const stickyOffsetContentTop = stickyOffset?.stickyOffsetContentTop || 0;

  return {
    stickyOffsetTop: Number(stickyOffsetTop) || 0,
    stickyOffsetContentTop: Number(stickyOffsetContentTop) || 0,
  };
}

// Enhanced loadStickyFilterTree with persisted offset support
export async function loadStickyFilterTree(
  context: Context,
  override?: StickyOverrideConfig
): Promise<{
  controller: DynamicStickyController | undefined;
  cleanup: () => void;
}> {
  const { paginationType = 'default' } =
    getThemeSettings(context)?.additionalElements?.pagination || {};

  // Get persisted offset values
  const { stickyOffsetContentTop } = getPersistedOffsetValues(context);

  let controller: DynamicStickyController | undefined;

  const baseConfig: StickyConfig = {
    elements: {
      stickyElement: CONSTANTS.SELECTORS.FILTER_TREE_VERTICAL_CONTENT,
      endElement: CONSTANTS.SELECTORS.END_ELEMENT,
      skipSelector: [CONSTANTS.SELECTORS.FILTER_TREE_VERTICAL_CONTENT_CLASS],
    },
    styling: {
      isMaxWidth: true,
      customStyle: 'overflow: auto',
    },
    behavior: {
      ignoreCheckWidth: false,
      isToolbarIndex: false,
      watchForDOMChanges: true,
      // Use persisted offset if available and sticky filter is enabled on mobile
      defaultOffsetTop: stickyOffsetContentTop ? stickyOffsetContentTop : undefined,
    },
    hooks: {
      callBack: (isSticky: boolean) => {
        const layout = qs('.boost-sd-layout') as HTMLElement | null;
        const left = qs('.boost-sd-left') as HTMLElement | null;

        if (isSticky) {
          if (paginationType === 'default') {
            layout?.setAttribute('style', `min-height: ${left?.scrollHeight || 0}px`);
          } else {
            left?.setAttribute('style', 'position: static');
          }
          document.body.classList.add(CONSTANTS.SELECTORS.STICKY_BODY);
        } else {
          layout?.removeAttribute('style');
          document.body.classList.remove(CONSTANTS.SELECTORS.STICKY_BODY);
        }
      },
      onElementsChanged: (newElements, oldElements) => {
        if (controller?.getConfig()?.debug) {
          console.debug('[sticky] Filter tree elements changed:', {
            old: oldElements.length,
            new: newElements.length,
          });
        }
      },
    },
    debug: shouldDebug(),
  };

  const config = mergeConfig(baseConfig, override);

  try {
    controller = new DynamicStickyController(config);

    const success = controller.initialize();

    if (!success) {
      console.warn('Failed to initialize dynamic sticky filter tree');
      controller = undefined;
    }
  } catch (error) {
    console.error('Error initializing dynamic sticky filter tree:', error);
    controller = undefined;
  }

  return {
    controller,
    cleanup: () => {
      controller?.destroy();
      document.body.classList.remove(CONSTANTS.SELECTORS.STICKY_BODY);
      qs('.boost-sd-layout')?.removeAttribute('style');
    },
  };
}

// Enhanced loadStickyFilterTreeExpand with persisted offset support
export async function loadStickyFilterTreeExpand(
  context: Context,
  override?: StickyOverrideConfig
): Promise<{
  controller: DynamicStickyController | undefined;
  cleanup: () => void;
}> {
  const { paginationType = 'default' } =
    getThemeSettings(context)?.additionalElements?.pagination || {};

  // Get persisted offset values
  const { stickyOffsetTop } = getPersistedOffsetValues(context);

  let controller: DynamicStickyController | undefined;

  const baseConfig: StickyConfig = {
    elements: {
      stickyElement: CONSTANTS.SELECTORS.TOOLBAR_CONTAINER,
      endElement: CONSTANTS.SELECTORS.END_ELEMENT,
      skipSelector: [CONSTANTS.SELECTORS.TOOLBAR_CONTAINER_CLASS],
    },
    styling: {
      isFullWidth: true,
      isMaxWidth: false,
    },
    behavior: {
      isToolbarIndex: true,
      watchForDOMChanges: true,
      // Use persisted offset for toolbar (mobile) if available
      defaultOffsetTop: stickyOffsetTop ? stickyOffsetTop : undefined,
    },
    hooks: {
      callBack: (isSticky: boolean) => {
        const layout = qs('.boost-sd-layout') as HTMLElement | null;
        const left = qs('.boost-sd-left') as HTMLElement | null;
        const toolbar = qs(CONSTANTS.SELECTORS.TOOLBAR_CONTAINER) as HTMLElement | null;

        if (isSticky) {
          if (paginationType !== 'default') {
            left?.setAttribute('style', 'position: static;');
          }
          layout?.setAttribute('style', `padding-top: ${toolbar?.clientHeight || 0}px;`);
          toolbar?.classList.add(CONSTANTS.SELECTORS.TOOLBAR_STICKY_BODY);
        } else {
          layout?.removeAttribute('style');
          toolbar?.classList.remove(CONSTANTS.SELECTORS.TOOLBAR_STICKY_BODY);
        }
      },
      onElementsChanged: (newElements, oldElements) => {
        if (controller?.getConfig()?.debug) {
          console.debug('[sticky] Toolbar elements changed:', {
            old: oldElements.length,
            new: newElements.length,
          });
        }
      },
    },
    debug: shouldDebug(),
  };

  const config = mergeConfig(baseConfig, override);

  try {
    controller = new DynamicStickyController(config);

    const success = controller.initialize();

    if (!success) {
      console.warn('Failed to initialize dynamic sticky toolbar');
      controller = undefined;
    }
  } catch (error) {
    console.error('Error initializing dynamic sticky toolbar:', error);
    controller = undefined;
  }

  return {
    controller,
    cleanup: () => {
      controller?.destroy();
      const layout = qs('.boost-sd-layout') as HTMLElement | null;
      const toolbar = qs(CONSTANTS.SELECTORS.TOOLBAR_CONTAINER) as HTMLElement | null;

      layout?.removeAttribute('style');
      toolbar?.classList.remove(CONSTANTS.SELECTORS.TOOLBAR_STICKY_BODY);
    },
  };
}

export async function loadStickyFilterTreeExpandContent(
  _: Context,
  override?: StickyOverrideConfig
): Promise<{
  controller: DynamicStickyController | undefined;
  cleanup: () => void;
}> {
  let controller: DynamicStickyController | undefined;

  const baseConfig: StickyConfig = {
    elements: {
      stickyElement: CONSTANTS.SELECTORS.FILTER_TREE_WRAPPER_VERTICAL_CONTENT,
      endElement: CONSTANTS.SELECTORS.END_ELEMENT,
      skipSelector: [CONSTANTS.SELECTORS.FILTER_TREE_VERTICAL_CONTENT_CLASS],
      preDefineSelector: CONSTANTS.SELECTORS.TOOLBAR_CONTAINER,
    },
    styling: {
      isMaxWidth: false,
      customStyle: 'overflow: auto',
    },
    behavior: {
      ignoreCheckWidth: true,

      isToolbarIndex: true,
      watchForDOMChanges: true,
    },
    hooks: {
      callBack: (isSticky: boolean) => {
        if (isSticky) {
          document.body.classList.add(CONSTANTS.SELECTORS.STICKY_BODY);
        } else {
          document.body.classList.remove(CONSTANTS.SELECTORS.STICKY_BODY);
          const layout = qs('.boost-sd-layout') as HTMLElement | null;
          layout?.style.removeProperty('min-height');
        }
      },
      onElementsChanged: (newElements, oldElements) => {
        if (controller?.getConfig()?.debug) {
          console.debug('[sticky] Toolbar elements changed:', {
            old: oldElements.length,
            new: newElements.length,
          });
        }
      },
    },
    debug: shouldDebug(),
  };

  const config = mergeConfig(baseConfig, override);

  try {
    controller = new DynamicStickyController(config);

    const success = controller.initialize();

    if (!success) {
      console.warn('Failed to initialize dynamic sticky filter tree');
      controller = undefined;
    }
  } catch (error) {
    console.error('Error initializing dynamic sticky filter tree:', error);
    controller = undefined;
  }

  return {
    controller,
    cleanup: () => {
      controller?.destroy();
      document.body.classList.remove(CONSTANTS.SELECTORS.STICKY_BODY);
      const layout = qs('.boost-sd-layout') as HTMLElement | null;
      layout?.style.removeProperty('min-height');
    },
  };
}

// Enhanced loadStickyFilterTreeOffCanvas with persisted offset support
export async function loadStickyFilterTreeOffCanvas(
  context: Context,
  override?: StickyOverrideConfig
): Promise<{
  controller: DynamicStickyController | undefined;
  cleanup: () => void;
}> {
  // Get persisted offset values
  const { stickyOffsetTop } = getPersistedOffsetValues(context);

  let controller: DynamicStickyController | undefined;

  const baseConfig: StickyConfig = {
    elements: {
      stickyElement: CONSTANTS.SELECTORS.TOOLBAR_CONTAINER,
      endElement: CONSTANTS.SELECTORS.END_ELEMENT,
      skipSelector: [
        CONSTANTS.SELECTORS.FILTER_TREE_VERTIAL_OFF_CANVAS,
        CONSTANTS.SELECTORS.TOOLBAR_CONTAINER,
      ],
    },
    styling: {
      isFullWidth: true,
      isMaxWidth: false,
    },
    behavior: {
      isToolbarIndex: true,
      watchForDOMChanges: true,
      // Use persisted offset for off-canvas toolbar if available
      defaultOffsetTop: stickyOffsetTop ? stickyOffsetTop : undefined,
    },
    hooks: {
      callBack: (isSticky: boolean) => {
        const toolbar = qs(CONSTANTS.SELECTORS.TOOLBAR_CONTAINER) as HTMLElement | null;
        const right = qs('.boost-sd-right') as HTMLElement | null;

        if (isSticky) {
          toolbar?.classList.add(CONSTANTS.SELECTORS.TOOLBAR_STICKY_BODY);
          right?.setAttribute('style', `padding-top: ${toolbar?.clientHeight || 0}px`);
        } else {
          toolbar?.classList.remove(CONSTANTS.SELECTORS.TOOLBAR_STICKY_BODY);
          right?.removeAttribute('style');
        }
      },
      onElementsChanged: (newElements, oldElements) => {
        if (controller?.getConfig()?.debug) {
          console.debug('[sticky] Off-canvas toolbar elements changed:', {
            old: oldElements.length,
            new: newElements.length,
          });
        }
      },
    },
    debug: shouldDebug(),
  };

  const config = mergeConfig(baseConfig, override);

  try {
    controller = new DynamicStickyController(config);

    const success = controller.initialize();

    if (!success) {
      console.warn('Failed to initialize dynamic sticky off-canvas toolbar');
      controller = undefined;
    }
  } catch (error) {
    console.error('Error initializing dynamic sticky off-canvas toolbar:', error);
    controller = undefined;
  }

  return {
    controller,
    cleanup: () => {
      controller?.destroy();
      const toolbar = qs(CONSTANTS.SELECTORS.TOOLBAR_CONTAINER) as HTMLElement | null;
      const right = qs('.boost-sd-right') as HTMLElement | null;

      toolbar?.classList.remove(CONSTANTS.SELECTORS.TOOLBAR_STICKY_BODY);
      right?.removeAttribute('style');
    },
  };
}

export async function loadStickyFilterTreeHorizontal(
  _: Context,
  override?: StickyOverrideConfig
): Promise<{
  controller: DynamicStickyController | undefined;
  cleanup: () => void;
}> {
  let controller: DynamicStickyController | undefined;

  const baseConfig: StickyConfig = {
    elements: {
      stickyElement: CONSTANTS.SELECTORS.FILTER_TREE_HORIZONTAL_CONTENT,
      endElement: CONSTANTS.SELECTORS.END_ELEMENT,
      skipSelector: [CONSTANTS.SELECTORS.FILTER_TREE_HORIZONTAL_CONTENT],
    },
    styling: {
      isFullWidth: true,
    },
    behavior: {
      isToolbarIndex: true,
      watchForDOMChanges: true,
    },
    hooks: {
      callBack: (isSticky) => {
        if (isSticky) {
          document.body.classList.add(CONSTANTS.SELECTORS.HORIZONTAL_STICKY_BODY);
        } else {
          document.body.classList.remove(CONSTANTS.SELECTORS.HORIZONTAL_STICKY_BODY);
        }

        const right = qs('.boost-sd-right') as HTMLElement | null;
        const toolbar = qs(CONSTANTS.SELECTORS.TOOLBAR_CONTAINER) as HTMLElement | null;

        if (isSticky) {
          right?.setAttribute('style', `padding-top: ${toolbar?.clientHeight || 0}px`);
        } else {
          right?.removeAttribute('style');
        }
      },
      onElementsChanged: (newElements, oldElements) => {
        if (controller?.getConfig()?.debug) {
          console.debug('[sticky] Horizontal content elements changed:', {
            old: oldElements.length,
            new: newElements.length,
          });
        }
      },
    },
    debug: shouldDebug(),
  };

  const config = mergeConfig(baseConfig, override);

  try {
    controller = new DynamicStickyController(config);

    const success = controller.initialize();

    if (!success) {
      console.warn('Failed to initialize dynamic sticky horizontal content');
      controller = undefined;
    }
  } catch (error) {
    console.error('Error initializing dynamic sticky horizontal content:', error);
    controller = undefined;
  }

  return {
    controller,
    cleanup: () => {
      controller?.destroy();
      const right = qs('.boost-sd-right') as HTMLElement | null;
      right?.removeAttribute('style');
      document.body.classList.remove(CONSTANTS.SELECTORS.HORIZONTAL_STICKY_BODY);
    },
  };
}

export async function loadStickyFilterTreeHorizontalToolbar(
  context: Context,
  override?: StickyOverrideConfig
) {
  const { filterLayout, filterTreeHorizontalStyle } = getFilterSettings(context);
  const filterTreeVisible =
    !isMobileWidth() &&
    filterLayout === 'horizontal' &&
    ['style1', 'style2', 'style3'].includes(filterTreeHorizontalStyle);

  let controller: DynamicStickyController | undefined;

  const baseConfig: StickyConfig = {
    elements: {
      stickyElement: CONSTANTS.SELECTORS.TOOLBAR_CONTAINER_EXPAND,
      endElement: CONSTANTS.SELECTORS.END_ELEMENT,
      skipSelector: [CONSTANTS.SELECTORS.TOOLBAR_CONTAINER_EXPAND],
    },
    styling: {
      isFullWidth: true,
    },
    behavior: {
      isToolbarIndex: true,
      watchForDOMChanges: true,
      isOpeningFilterTree: filterTreeVisible,
    },
    hooks: {
      callBack: (isSticky) => {
        const right = qs('.boost-sd-right') as HTMLElement | null;
        const toolbar = qs(CONSTANTS.SELECTORS.TOOLBAR_CONTAINER) as HTMLElement | null;

        if (isSticky) {
          right?.setAttribute('style', `padding-top: ${toolbar?.clientHeight || 0}px`);
          toolbar?.classList.add(CONSTANTS.SELECTORS.TOOLBAR_STICKY_BODY);
        } else {
          right?.removeAttribute('style');
          toolbar?.classList.remove(CONSTANTS.SELECTORS.TOOLBAR_STICKY_BODY);
        }
      },
      onElementsChanged: (newElements, oldElements) => {
        if (controller?.getConfig()?.debug) {
          console.debug('[sticky] Horizontal toolbar elements changed:', {
            old: oldElements.length,
            new: newElements.length,
          });
        }
      },
    },
    debug: shouldDebug(),
  };

  const config = mergeConfig(baseConfig, override);

  try {
    controller = new DynamicStickyController(config);

    const success = controller.initialize();

    if (!success) {
      console.warn('Failed to initialize dynamic sticky horizontal toolbar');
      controller = undefined;
    }
  } catch (error) {
    console.error('Error initializing dynamic sticky horizontal toolbar:', error);
    controller = undefined;
  }

  return {
    controller,
    cleanup: () => {
      controller?.destroy();
      const right = qs('.boost-sd-right') as HTMLElement | null;
      const toolbar = qs(CONSTANTS.SELECTORS.TOOLBAR_CONTAINER) as HTMLElement | null;
      toolbar?.classList.remove(CONSTANTS.SELECTORS.TOOLBAR_STICKY_BODY);
      right?.removeAttribute('style');
    },
  };
}
export async function loadStickyFilterTreeHorizontalPanel(
  _: Context,
  override?: StickyOverrideConfig
) {
  let controller: DynamicStickyController | undefined;

  const baseConfig: StickyConfig = {
    elements: {
      stickyElement: CONSTANTS.SELECTORS.FILTER_TREE_HORIZONTAL,
      endElement: CONSTANTS.SELECTORS.BODY,
      skipSelector: [CONSTANTS.SELECTORS.FILTER_TREE_HORIZONTAL],
      preDefineSelector: CONSTANTS.SELECTORS.TOOLBAR_CONTAINER_EXPAND,
    },
    styling: {
      isFullWidth: true,
      customStyle:
        'overflow-y: auto; overflow-x: hidden; border-top: 1px solid #ebebeb; border-bottom: 1px solid #ebebeb;',
    },
    behavior: {
      isToolbarIndex: false,
      watchForDOMChanges: true,
    },
    hooks: {
      callBack: (isSticky) => {
        if (isSticky) {
          document.body.classList.add(CONSTANTS.SELECTORS.HORIZONTAL_STICKY_BODY);
        } else {
          document.body.classList.remove(CONSTANTS.SELECTORS.HORIZONTAL_STICKY_BODY);
        }
      },
      onElementsChanged: (newElements, oldElements) => {
        if (controller?.getConfig()?.debug) {
          console.debug('[sticky] Horizontal panel elements changed:', {
            old: oldElements.length,
            new: newElements.length,
          });
        }
      },
    },
    debug: shouldDebug(),
  };

  const config = mergeConfig(baseConfig, override);

  try {
    controller = new DynamicStickyController(config);

    const success = controller.initialize();

    if (!success) {
      console.warn('Failed to initialize dynamic sticky horizontal panel');
      controller = undefined;
    }
  } catch (error) {
    console.error('Error initializing dynamic sticky horizontal panel:', error);
    controller = undefined;
  }

  return {
    controller,
    cleanup: () => {
      controller?.destroy();
      const layout = qs('.boost-sd-layout') as HTMLElement | null;
      const toolbar = qs(CONSTANTS.SELECTORS.TOOLBAR_CONTAINER) as HTMLElement | null;

      layout?.removeAttribute('style');
      toolbar?.classList.remove(CONSTANTS.SELECTORS.TOOLBAR_STICKY_BODY);
    },
  };
}
export async function loadStickyFilterTreeHorizontalContent(
  _: Context,
  override?: StickyOverrideConfig
) {
  let controller: DynamicStickyController | undefined;

  const baseConfig: StickyConfig = {
    elements: {
      stickyElement: CONSTANTS.SELECTORS.FILTER_TREE_HORIZONTAL_CONTENT,
      endElement: CONSTANTS.SELECTORS.BODY,
      skipSelector: [CONSTANTS.SELECTORS.FILTER_TREE_HORIZONTAL_CONTENT],
    },
    styling: {
      isFullHeight: true,
      customStyle: 'position: static;',
    },
    behavior: {
      isToolbarIndex: false,
      watchForDOMChanges: true,
    },
    hooks: {
      onElementsChanged: (newElements, oldElements) => {
        if (controller?.getConfig()?.debug) {
          console.debug('[sticky] Horizontal content elements changed:', {
            old: oldElements.length,
            new: newElements.length,
          });
        }
      },
    },
    debug: shouldDebug(),
  };

  const config = mergeConfig(baseConfig, override);

  try {
    controller = new DynamicStickyController(config);

    const success = controller.initialize();

    if (!success) {
      console.warn('Failed to initialize dynamic sticky horizontal content');
      controller = undefined;
    }
  } catch (error) {
    console.error('Error initializing dynamic sticky horizontal content:', error);
    controller = undefined;
  }

  return {
    controller,
    cleanup: () => {
      controller?.destroy();
      const layout = qs('.boost-sd-layout') as HTMLElement | null;
      const toolbar = qs(CONSTANTS.SELECTORS.TOOLBAR_CONTAINER) as HTMLElement | null;

      layout?.removeAttribute('style');
      toolbar?.classList.remove(CONSTANTS.SELECTORS.TOOLBAR_STICKY_BODY);
    },
  };
}

//#region STICKY MOBILE
// Enhanced loadStickyFilterTreeMobile with persisted offset support
export async function loadStickyFilterTreeMobile(
  context: Context,
  override?: StickyOverrideConfig
): Promise<{
  controller: DynamicStickyController | undefined;
  cleanup: () => void;
}> {
  // Get persisted offset values
  const { stickyOffsetTop } = getPersistedOffsetValues(context);

  let controller: DynamicStickyController | undefined;

  const baseConfig: StickyConfig = {
    elements: {
      stickyElement: CONSTANTS.SELECTORS.TOOLBAR_MOBILE,
      endElement: CONSTANTS.SELECTORS.END_ELEMENT,
      skipSelector: [CONSTANTS.SELECTORS.TOOLBAR_MOBILE],
    },
    styling: {
      isFullWidth: true,
      isMaxWidth: true,
    },
    behavior: {
      isToolbarIndex: true,
      watchForDOMChanges: true,
      // Use persisted offset for off-canvas toolbar if available
      defaultOffsetTop: stickyOffsetTop ? stickyOffsetTop : undefined,
    },
    hooks: {
      onElementsChanged: (newElements, oldElements) => {
        if (controller?.getConfig()?.debug) {
          console.debug('[sticky] Mobile default toolbar elements changed:', {
            old: oldElements.length,
            new: newElements.length,
          });
        }
      },
    },
    debug: shouldDebug(),
  };

  const config = mergeConfig(baseConfig, override);

  try {
    controller = new DynamicStickyController(config);

    const success = controller.initialize();

    if (!success) {
      console.warn('Failed to initialize dynamic sticky mobile default toolbar');
      controller = undefined;
    }
  } catch (error) {
    console.error('Error initializing dynamic sticky mobile toolbar:', error);
    controller = undefined;
  }

  return {
    controller,
    cleanup: () => {
      controller?.destroy();
    },
  };
}

// Enhanced loadStickyFilterTreeMobile with persisted offset support
export async function loadStickyFilterTreeMobileExpand(
  context: Context,
  override?: StickyOverrideConfig
): Promise<{
  controller: DynamicStickyController | undefined;
  cleanup: () => void;
}> {
  // Get persisted offset values
  const { stickyOffsetTop } = getPersistedOffsetValues(context);
  const { filterLayout, filterTreeVerticalStyle } = getFilterSettings(context);

  const filterTreeVisible =
    !isMobileWidth() && filterLayout === 'vertical' && filterTreeVerticalStyle === 'style-default';
  const [, setStickyOpenStatus] = context.useContextState('stickyOpenStatus', 'false');

  let controller: DynamicStickyController | undefined;

  const baseConfig: StickyConfig = {
    elements: {
      stickyElement: CONSTANTS.SELECTORS.TOOLBAR_MOBILE,
      endElement: CONSTANTS.SELECTORS.END_ELEMENT,
      skipSelector: [CONSTANTS.SELECTORS.TOOLBAR_MOBILE],
    },
    styling: {
      isFullWidth: true,
      isMaxWidth: true,
    },
    behavior: {
      isToolbarIndex: true,
      watchForDOMChanges: true,
      isOpeningFilterTree: filterTreeVisible,
      // Use persisted offset for off-canvas toolbar if available
      defaultOffsetTop: stickyOffsetTop ? stickyOffsetTop : undefined,
    },
    hooks: {
      callBack: (isSticky) => {
        setStickyOpenStatus(`${isSticky}`);
      },
      onElementsChanged: (newElements, oldElements) => {
        if (controller?.getConfig()?.debug) {
          console.debug('[sticky] Mobile default toolbar elements changed:', {
            old: oldElements.length,
            new: newElements.length,
          });
        }
      },
    },
    debug: shouldDebug(),
  };

  const config = mergeConfig(baseConfig, override);

  try {
    controller = new DynamicStickyController(config);

    const success = controller.initialize();

    if (!success) {
      console.warn('Failed to initialize dynamic sticky mobile expand default toolbar');
      controller = undefined;
    }
  } catch (error) {
    console.error('Error initializing dynamic sticky mobile expand toolbar:', error);
    controller = undefined;
  }

  return {
    controller,
    cleanup: () => {
      controller?.destroy();
    },
  };
}

export async function loadStickyFilterTreeMobileExpandContent(
  context: Context,
  override?: StickyOverrideConfig
): Promise<{
  controller: DynamicStickyController | undefined;
  cleanup: () => void;
}> {
  // Get persisted offset values
  const { stickyOffsetContentTop } = getPersistedOffsetValues(context);

  let controller: DynamicStickyController | undefined;

  const baseConfig: StickyConfig = {
    elements: {
      stickyElement: CONSTANTS.SELECTORS.FILTER_TREE_VERTICAL_CONTENT_CLASS,
      endElement: CONSTANTS.SELECTORS.BODY,
      skipSelector: [CONSTANTS.SELECTORS.FILTER_TREE_VERTICAL_CONTENT_CLASS],
      preDefineSelector: CONSTANTS.SELECTORS.TOOLBAR_MOBILE,
    },
    styling: {
      isFullWidth: true,
      isMaxWidth: true,
      customStyle: 'overflow: auto; padding: 16px;',
    },
    behavior: {
      ignoreCheckWidth: true,
      isToolbarIndex: true,
      watchForDOMChanges: true,
      // Use persisted offset for off-canvas toolbar if available
      defaultOffsetTop: stickyOffsetContentTop ? stickyOffsetContentTop : undefined,
    },
    hooks: {
      callBack: (isSticky) => {
        if (isSticky) {
          document.body.classList.add(CONSTANTS.CSS_CLASSES.STICKY_STYLE_EXPAND);
        } else {
          document.body.classList.remove(CONSTANTS.CSS_CLASSES.STICKY_STYLE_EXPAND);
        }
      },
      onElementsChanged: (newElements, oldElements) => {
        if (controller?.getConfig()?.debug) {
          console.debug('[sticky] Mobile expand content toolbar elements changed:', {
            old: oldElements.length,
            new: newElements.length,
          });
        }
      },
    },
    debug: shouldDebug(),
  };

  const config = mergeConfig(baseConfig, override);

  try {
    controller = new DynamicStickyController(config);

    const success = controller.initialize();

    if (!success) {
      console.warn('Failed to initialize dynamic sticky mobile expand content toolbar');
      controller = undefined;
    }
  } catch (error) {
    console.error('Error initializing dynamic sticky expand content toolbar:', error);
    controller = undefined;
  }

  return {
    controller,
    cleanup: () => {
      controller?.destroy();
    },
  };
}

//#endregion

export function initializeStickyVertical(
  context: Context,
  override?: StickyOverrideConfig
): {
  loadStickyFilterTree: () => Promise<{
    controller: DynamicStickyController | undefined;
    cleanup: () => void;
  }>;
  loadStickyFilterTreeExpand: (override?: StickyOverrideConfig) => Promise<{
    controller: DynamicStickyController | undefined;
    cleanup: () => void;
  }>;
  loadStickyFilterTreeExpandContent: () => Promise<{
    controller: DynamicStickyController | undefined;
    cleanup: () => void;
  }>;
  loadStickyFilterTreeOffCanvas: () => Promise<{
    controller: DynamicStickyController | undefined;
    cleanup: () => void;
  }>;
} {
  // Run persistDataOffsetTop first to ensure offset values are set
  persistDataOffsetTop(context);

  return {
    loadStickyFilterTree: () => loadStickyFilterTree(context, override),
    loadStickyFilterTreeExpand: (override?: StickyOverrideConfig) =>
      loadStickyFilterTreeExpand(context, override),
    loadStickyFilterTreeExpandContent: () => loadStickyFilterTreeExpandContent(context, override),
    loadStickyFilterTreeOffCanvas: () => loadStickyFilterTreeOffCanvas(context, override),
  };
}

export function initializeStickyMobile(
  context: Context,
  override?: StickyOverrideConfig
): {
  loadStickyFilterTreeMobile: () => Promise<{
    controller: DynamicStickyController | undefined;
    cleanup: () => void;
  }>;
  loadStickyFilterTreeMobileExpand: () => Promise<{
    controller: DynamicStickyController | undefined;
    cleanup: () => void;
  }>;
  loadStickyFilterTreeMobileExpandContent: () => Promise<{
    controller: DynamicStickyController | undefined;
    cleanup: () => void;
  }>;
} {
  // Run persistDataOffsetTop first to ensure offset values are set
  persistDataOffsetTop(context);
  return {
    loadStickyFilterTreeMobile: () => loadStickyFilterTreeMobile(context, override),
    loadStickyFilterTreeMobileExpand: () => loadStickyFilterTreeMobileExpand(context, override),
    loadStickyFilterTreeMobileExpandContent: () =>
      loadStickyFilterTreeMobileExpandContent(context, override),
  };
}

export function initializeStickyHorizontal(
  context: Context,
  override?: StickyOverrideConfig
): {
  loadStickyFilterTreeHorizontal: () => Promise<{
    controller: DynamicStickyController | undefined;
    cleanup: () => void;
  }>;
  loadStickyFilterTreeHorizontalToolbar: (override?: StickyOverrideConfig) => Promise<{
    controller: DynamicStickyController | undefined;
    cleanup: () => void;
  }>;
  loadStickyFilterTreeHorizontalPanel: (override?: StickyOverrideConfig) => Promise<{
    controller: DynamicStickyController | undefined;
    cleanup: () => void;
  }>;
  loadStickyFilterTreeHorizontalContent: () => Promise<{
    controller: DynamicStickyController | undefined;
    cleanup: () => void;
  }>;
} {
  // Run persistDataOffsetTop first to ensure offset values are set
  persistDataOffsetTop(context);
  return {
    loadStickyFilterTreeHorizontal: () => loadStickyFilterTreeHorizontal(context, override),
    loadStickyFilterTreeHorizontalToolbar: (override?: StickyOverrideConfig) =>
      loadStickyFilterTreeHorizontalToolbar(context, override),
    loadStickyFilterTreeHorizontalPanel: (override?: StickyOverrideConfig) =>
      loadStickyFilterTreeHorizontalPanel(context, override),
    loadStickyFilterTreeHorizontalContent: (override?: StickyOverrideConfig) =>
      loadStickyFilterTreeHorizontalContent(context, override),
  };
}

// Merge function for sticky config
function mergeConfig(base: StickyConfig, override?: StickyOverrideConfig): StickyConfig {
  if (!override) return base;

  const merged: StickyConfig = {
    elements: { ...base.elements },
    styling: { ...base.styling, ...override.styling },
    behavior: { ...base.behavior, ...override.behavior },
    hooks: { ...base.hooks, ...override.hooks },
    debug: override.debug ?? base.debug,
  };

  // Handle elements separately to support skipSelector merge modes
  if (override.elements) {
    const { skipSelector, skipSelectorMergeMode, ...otherElements } = override.elements;

    // Merge other element properties normally
    Object.assign(merged.elements, otherElements);

    // Handle skipSelector with merge mode
    if (skipSelector) {
      const mode = skipSelectorMergeMode || 'merge'; // Default to merge

      switch (mode) {
        case 'replace':
          // Completely replace the base skipSelector list
          merged.elements.skipSelector = [...skipSelector];
          break;

        case 'prepend':
          // Add custom selectors at the beginning (higher priority)
          merged.elements.skipSelector = [...skipSelector, ...(base.elements.skipSelector || [])];
          break;

        case 'merge':
        default:
          // Add custom selectors at the end (standard merge)
          merged.elements.skipSelector = [...(base.elements.skipSelector || []), ...skipSelector];
          break;
      }
    }
  }

  return merged;
}
